"use client";

import { useTradePanel } from "@/components/trade-panel/hooks/use-trade-panel";
import { cn } from "@/lib/utils";
import { useEventConnect } from "@/store/eventConnect";
import { resolveUnitPrice } from "@/utils/price";
import { formatUnitPrice, get, getOptionLabel } from "@/utils/utils";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

interface TradeButtonBarProps {
    guess: any;
    event: any;
    isPreview?: boolean;
    hasSellableShares: boolean;
    onRefreshHoldings: () => void;
}

/**
 * 交易按钮栏组件
 * 显示底部的 Buy Yes、Buy No 和 More 按钮
 */
export function TradeButtonBar(
    { guess, event, isPreview = false, hasSellableShares, onRefreshHoldings }: TradeButtonBarProps,
) {
    const { open } = useTradePanel();
    const pathname = usePathname();
    const [ isMoreMenuOpen, setIsMoreMenuOpen ] = useState( false );
    const moreMenuRef = useRef<HTMLDivElement | null>( null );

    const guessNo = get( guess, "guessNo", "" );
    const marketPrice = useEventConnect( state => state.guesses[guessNo] );

    const yesRealtimePrice = marketPrice?.yesPrice ?? resolveUnitPrice( get( guess, "yesRealtimePrice" ) );
    const noRealtimePrice = marketPrice?.noPrice ?? resolveUnitPrice( get( guess, "noRealtimePrice" ) );

    const handleOpenTradeModal = ( selectedOption: "yes" | "no" ) => {
        if ( isPreview ) return;

        open( {
            eventInfo: event,
            guessInfo: guess,
            selectedOption,
            tradeType: "buy",
            pathname,
            onSuccess: onRefreshHoldings,
        } );
    };

    const handleBuyShares = () => {
        setIsMoreMenuOpen( false );
        open( {
            eventInfo: event,
            guessInfo: guess,
            selectedOption: "yes",
            tradeType: "buy",
            pathname,
            onSuccess: onRefreshHoldings,
        } );
    };

    const handleSellShares = () => {
        if ( !hasSellableShares ) return;
        setIsMoreMenuOpen( false );
        open( {
            eventInfo: event,
            guessInfo: guess,
            selectedOption: "yes",
            tradeType: "sell",
            pathname,
            onSuccess: onRefreshHoldings,
        } );
    };

    const handleSplitShares = () => {
        setIsMoreMenuOpen( false );
        open( {
            eventInfo: event,
            guessInfo: guess,
            selectedOption: "yes",
            tradeType: "split",
            pathname,
            onSuccess: onRefreshHoldings,
        } );
    };

    const handleMergeShares = () => {
        if ( !hasSellableShares ) return;
        setIsMoreMenuOpen( false );
        open( {
            eventInfo: event,
            guessInfo: guess,
            selectedOption: "yes",
            tradeType: "merge",
            pathname,
            onSuccess: onRefreshHoldings,
        } );
    };

    useEffect( () => {
        if ( !isMoreMenuOpen ) return;

        const handlePointerDown = ( event: MouseEvent | TouchEvent ) => {
            if ( !moreMenuRef.current ) return;
            const target = event.target as Node;
            if ( !moreMenuRef.current.contains( target ) ) {
                setIsMoreMenuOpen( false );
            }
        };

        document.addEventListener( "mousedown", handlePointerDown );
        document.addEventListener( "touchstart", handlePointerDown );

        return () => {
            document.removeEventListener( "mousedown", handlePointerDown );
            document.removeEventListener( "touchstart", handlePointerDown );
        };
    }, [ isMoreMenuOpen ] );

    return (
        <div className="w-full bg-surface-card border-t border-stroke">
            <div className="relative flex w-full items-center gap-3 py-3 px-4" ref={moreMenuRef}>
                <button
                    onClick={( e ) => {
                        e.stopPropagation();
                        handleOpenTradeModal( "yes" );
                    }}
                    disabled={isPreview}
                    className={cn(
                        "flex-1 min-w-0 h-12 rounded-md flex items-center justify-center gap-1 px-4 text-base font-[600] font-inter capitalize leading-normal text-center transition-all origin-bottom shadow-[0_-3px_0_0_rgba(0,0,0,0.25)_inset]",
                        isPreview ? "opacity-40 cursor-not-allowed" : "active:scale-y-[0.9] active:shadow-none",
                        "bg-action-accent text-content-fixed-inverse",
                    )}
                >
                    <span className="min-w-0 truncate">Buy {getOptionLabel( "yes", guess, event )}</span>
                    <span className="flex-shrink-0">{formatUnitPrice( yesRealtimePrice, { precision: 1 } )}</span>
                </button>
                <button
                    onClick={( e ) => {
                        e.stopPropagation();
                        handleOpenTradeModal( "no" );
                    }}
                    disabled={isPreview}
                    className={cn(
                        "flex-1 min-w-0 h-12 rounded-md flex items-center justify-center gap-1 px-4 text-base font-[600] font-inter capitalize leading-normal text-center transition-all origin-bottom shadow-[0_-3px_0_0_rgba(0,0,0,0.25)_inset]",
                        isPreview ? "opacity-40 cursor-not-allowed" : "active:scale-y-[0.9] active:shadow-none",
                        "bg-no text-content-fixed-inverse",
                    )}
                >
                    <span className="min-w-0 truncate">Buy {getOptionLabel( "no", guess, event )}</span>

                    <span className="flex-shrink-0">{formatUnitPrice( noRealtimePrice, { precision: 1 } )}</span>
                </button>
                {
                    /* <button
                    type="button"
                    onClick={( e ) => {
                        e.stopPropagation();
                        setIsMoreMenuOpen( ( prev ) => !prev );
                    }}
                    className="flex h-12 w-12 items-center justify-center gap-1 rounded-md bg-[#EEE] p-3 text-lg text-t3 shadow-[0_-3px_0_0_rgba(0,0,0,0.25)_inset]"
                    aria-label="More actions"
                >
                    <MoreIcon />
                </button> */
                }
                {isMoreMenuOpen && (
                    <div className="absolute bottom-[calc(100%+8px)] right-0 w-[140px] rounded-[10px] bg-surface-card px-3 py-2 text-[13px] text-t1 shadow-[0_10px_20px_rgba(0,0,0,0.15)]">
                        <button
                            type="button"
                            onClick={handleBuyShares}
                            className="flex w-full items-center py-2 hover:bg-surface-subtle transition-colors"
                        >
                            Buy shares
                        </button>
                        <button
                            type="button"
                            onClick={handleSellShares}
                            disabled={!hasSellableShares}
                            className={cn(
                                "flex w-full items-center py-2 transition-colors",
                                hasSellableShares
                                    ? "hover:bg-surface-subtle cursor-pointer"
                                    : "opacity-40 cursor-not-allowed",
                            )}
                        >
                            Sell shares
                        </button>
                        <button
                            type="button"
                            onClick={handleMergeShares}
                            disabled={!hasSellableShares}
                            className={cn(
                                "flex w-full items-center py-2 transition-colors",
                                hasSellableShares
                                    ? "hover:bg-surface-subtle cursor-pointer"
                                    : "opacity-40 cursor-not-allowed",
                            )}
                        >
                            Merge shares
                        </button>
                        <button
                            type="button"
                            onClick={handleSplitShares}
                            className="flex w-full items-center py-2 hover:bg-surface-subtle transition-colors"
                        >
                            Split shares
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
